import { useCallback, useState, useEffect, useRef } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'
import { CustomerPreview } from '@/hooks/useCustomersPreviewFilter'

interface TourStep {
    selector: string
    title: string
    description: string
    position?: 'top' | 'bottom' | 'left' | 'right'
    conditionBadge?: string
    action?: () => void // Action to perform when reaching this step
    skipAutoNext?: boolean // Skip automatic progression to next step
}

interface UseSurveyCreateTourOptions {
    onSetDemoData?: (customers: CustomerPreview[]) => void
    onMoveToStep2?: () => void
    onClearDemoData?: () => void
    getCurrentStep?: () => number // Get the current form step
}

export const useSurveyCreateTour = (options?: UseSurveyCreateTourOptions) => {
    const [isActive, setIsActive] = useState(false)
    const [currentTourStep, setCurrentTourStep] = useState(0)
    const isDemoMode = useRef(false)
    const goToStepRef = useRef<(stepIndex: number) => void>()
    const waitingForStep2 = useRef(false)

    // Demo customers for tour
    const demoCustomers: CustomerPreview[] = [
        {
            customer_id: 99991,
            customer_name: 'Công ty Demo ABC',
            customer_email: 'demo1@abc.com',
            company_name: 'Công ty TNHH Demo ABC',
            customer_phone: '0901234567',
            customer_status: 'active',
        },
        {
            customer_id: 99992,
            customer_name: 'Công ty Demo XYZ',
            customer_email: 'demo2@xyz.com',
            company_name: 'Công ty CP Demo XYZ',
            customer_phone: '0907654321',
            customer_status: 'active',
        },
        {
            customer_id: 99993,
            customer_name: 'Công ty Demo 123',
            customer_email: 'demo3@123.com',
            company_name: 'Công ty Demo 123',
            customer_phone: '0909876543',
            customer_status: 'trial',
        },
    ] as CustomerPreview[]

    const tourSteps: TourStep[] = [
        {
            selector: '[data-tour="survey-step-indicator"]',
            title: 'Hướng dẫn tạo khảo sát',
            description:
                'Quá trình tạo khảo sát bao gồm 2 bước: [Chọn khách hàng] và [Ghi nhận phản hồi].',
            position: 'bottom',
            action: () => {
                // Set demo data when starting tour
                if (options?.onSetDemoData && !isDemoMode.current) {
                    isDemoMode.current = true
                    options.onSetDemoData(demoCustomers)
                }
            },
        },
        {
            selector: '[data-tour="survey-customer-filters"]',
            title: 'Lọc khách hàng',
            description:
                'Lọc khách hàng theo các trường này. Mặc định là các khách hàng đang dùng dịch vụ.',
            position: 'bottom',
        },
        {
            selector: '[data-tour="survey-customer-preview"]',
            title: 'Chọn khách hàng',
            description:
                'Chọn khách hàng muốn tạo khảo sát. Cho tour demo này, chúng tôi đã tự động chọn 3 khách hàng mẫu cho bạn.',
            position: 'bottom',
        },
        {
            selector: '[data-tour="survey-next-button"]',
            title: 'Chuyển sang bước 2',
            description:
                'Nhấn nút Tiếp theo để chuyển sang bước 2 và tiếp tục hướng dẫn.',
            position: 'top',
        },
        {
            selector: '[data-tour="survey-response-table"]',
            title: 'Ghi nhận phản hồi',
            description:
                'Ghi nhận phản hồi khảo sát cho từng khách hàng đã chọn.',
            position: 'bottom',
        },
        {
            selector: '[data-tour="survey-view-toggle"]',
            title: 'Chuyển đổi chế độ xem',
            description:
                'Chuyển đổi giữa chế độ xem bảng và chế độ xem dạng thẻ cho giao diện phản hồi.',
            position: 'left',
        },
        {
            selector: '[data-tour="survey-response-card"]',
            title: 'Thẻ phản hồi khách hàng',
            description:
                'Nhấn vào thẻ khách hàng hoặc hàng trong bảng để mở hộp thoại phản hồi và ghi nhận câu trả lời khảo sát của họ.',
            position: 'bottom',
        },
        {
            selector: '[data-tour="survey-back-button"]',
            title: 'Nút Quay lại',
            description:
                'Quay lại bước trước nếu bạn cần thay đổi lựa chọn khách hàng.',
            position: 'top',
        },
        {
            selector: '[data-tour="survey-submit-button"]',
            title: 'Nút Gửi',
            description:
                'Gửi tất cả phản hồi khảo sát sau khi đã hoàn thành ghi nhận cho tất cả khách hàng đã chọn. Hệ thống sẽ tạo bản ghi khảo sát cho từng khách hàng.',
            position: 'top',
        },
    ]

    // Define endTour first so it can be referenced
    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentTourStep(0)

        // Clear demo data when tour ends
        if (isDemoMode.current && options?.onClearDemoData) {
            options.onClearDemoData()
            isDemoMode.current = false
        }
    }, [options])

    const showTourUI = (step: TourStep, stepIndex: number) => {
        injectTourStyles()
        cleanupTourElements()
        createTourOverlay()

        // Execute step action if exists
        if (step.action) {
            step.action()
        }

        const element = document.querySelector(step.selector) as HTMLElement
        if (!element) {
            // If we're waiting for step 2 elements after user clicked Next
            if (
                waitingForStep2.current &&
                step.selector.includes('survey-response')
            ) {
                // Keep trying to find the element for step 2
                setTimeout(() => goToStep(stepIndex), 500)
                return
            }

            // Skip to next step if element not found
            if (stepIndex < tourSteps.length - 1) {
                const nextStepIndex = stepIndex + 1
                setTimeout(() => goToStep(nextStepIndex), 100)
            } else {
                endTour()
            }
            return
        }

        // Clear waiting flag once we find the element
        if (
            waitingForStep2.current &&
            step.selector.includes('survey-response')
        ) {
            waitingForStep2.current = false
        }

        element.classList.add('tour-highlight-element')

        element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        })

        // Build description with condition badge if present
        let fullDescription = step.description
        if (step.conditionBadge) {
            fullDescription = `<span style="display:inline-block;background:#f3e8ff;color:#7c3aed;padding:2px 8px;border-radius:4px;font-size:11px;margin-bottom:8px;">${step.conditionBadge}</span><br/>${step.description}`
        }

        // Wait for scroll then add popover
        setTimeout(() => {
            // Special handling for survey-next-button step
            const isNextButtonStep =
                step.selector === '[data-tour="survey-next-button"]'

            const popover = createTourPopover({
                title: step.title,
                description: fullDescription,
                currentStep: stepIndex + 1,
                totalSteps: tourSteps.length,
                onPrev:
                    stepIndex > 0 ? () => goToStep(stepIndex - 1) : undefined,
                onNext: step.skipAutoNext
                    ? () => {} // Empty function to prevent keyboard handler error
                    : () => {
                          if (stepIndex === tourSteps.length - 1) {
                              endTour()
                          } else if (isNextButtonStep) {
                              // For the Next button step, trigger the actual button click
                              // and set flag to wait for step 2 elements
                              waitingForStep2.current = true
                              const nextButton = document.querySelector(
                                  '[data-tour="survey-next-button"]'
                              ) as HTMLButtonElement
                              if (nextButton) {
                                  nextButton.click()
                                  // Move to step 2 form submission
                                  if (options?.onMoveToStep2) {
                                      options.onMoveToStep2()
                                  }
                                  // Start looking for the next tour step after DOM updates
                                  setTimeout(() => {
                                      const nextStepIndex = tourSteps.findIndex(
                                          (s) =>
                                              s.selector.includes(
                                                  'survey-response-table'
                                              )
                                      )
                                      if (nextStepIndex > -1) {
                                          goToStep(nextStepIndex)
                                      }
                                  }, 1000)
                              }
                          } else {
                              goToStep(stepIndex + 1)
                          }
                      },
                onClose: endTour,
                isLastStep: stepIndex === tourSteps.length - 1,
            })

            document.body.appendChild(popover)
            positionPopover(popover, element)
        }, 300)
    }

    const goToStep = (stepIndex: number) => {
        const step = tourSteps[stepIndex]
        if (!step) return

        cleanupTourElements()
        showTourUI(step, stepIndex)
        setCurrentTourStep(stepIndex)
    }

    // Assign goToStep to ref so it can be used in actions
    goToStepRef.current = goToStep

    const startTour = useCallback(() => {
        setIsActive(true)

        // Determine starting step based on current form step
        const currentFormStep = options?.getCurrentStep?.() || 1
        let startingTourStepIndex = 0

        if (currentFormStep === 2) {
            // If we're on step 2, find the first step 2 tour step
            const step2Index = tourSteps.findIndex((step) =>
                step.selector.includes('survey-response-table')
            )
            if (step2Index > -1) {
                startingTourStepIndex = step2Index
            }
        }

        setCurrentTourStep(startingTourStepIndex)

        // Wait longer for scroll to complete, then start from the appropriate step
        showTourUI(tourSteps[startingTourStepIndex], startingTourStepIndex)
    }, [options])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return {
        startTour,
        endTour,
        isActive,
        currentStep: currentTourStep,
        totalSteps: tourSteps.length,
    }
}
